<script setup lang="ts">
import { navLinks } from "~/utils/navLinks";
/**
 * DonateErrorStates — wrapper unifié des écrans d'erreur pour les pages
 * de don. Centralise la cascade de priorité entre les différentes
 * variantes d'indisponibilité :
 *
 *   1. Asso suspended/banned (Trust & Safety)  → <OrgUnavailable>
 *   2. Cagnotte en pause (paused)              → <DonateErrorState type="campaign-paused">
 *   3. QR / payment-link / cagnotte invalide
 *      et aucun provider de paiement actif      → "stripe-not-configured"
 *   4. QR / payment-link / cagnotte invalide
 *      et au moins un provider actif            → "qr-invalid" / "payment-link-invalid"
 *   5. Stripe init error (et pas en PayPal-only) → "stripe-error"
 *
 * Avant ce composant, les 4 pages de don dupliquaient cette cascade à la
 * main avec 3-4 `<DonateErrorState>` consécutifs + 1 `<OrgUnavailable>`
 * conditionnel. Drift garanti à chaque ajout d'un état d'erreur — on a
 * d'ailleurs constaté que `pay/[slug]` et `c/[slug]/payment` n'avaient
 * PAS `<OrgUnavailable>` alors que les 2 autres l'avaient.
 *
 * Le composant rend `null` quand aucune erreur n'est active → la page
 * affiche son contenu normal en `v-else`.
 *
 * Émet `@reload` quand l'utilisateur clique sur "Réessayer" dans
 * `<DonateErrorState>` — non émis pour `<OrgUnavailable>` (asso bloquée
 * = rechargement inutile).
 */
import { computed } from "vue";

type RiskStatus =
  | "clean"
  | "documents_requested"
  | "watch"
  | "restricted"
  | "suspended"
  | "banned";

interface Props {
  // ─── Org Trust & Safety (priorité 1) ───────────────────────────────
  /** Statut de risque de l'org. Si "suspended"/"banned" → OrgUnavailable. */
  orgRiskStatus?: RiskStatus | string | null;
  /** Raison brute (texte libre, max 500 chars) à afficher au visiteur. */
  orgRiskReason?: string | null;
  /** Nom de l'asso (affiché dans OrgUnavailable). */
  organizationName?: string | null;

  // ─── Cagnotte en pause (priorité 2) ────────────────────────────────
  /** True si la cagnotte est explicitement mise en pause par l'asso. */
  isCampaignPaused?: boolean;
  /** Message custom à afficher (sinon fallback générique). */
  campaignPausedMessage?: string;
  /** Action "Retour à la cagnotte" si présente, sinon "Réessayer". */
  onPausedBack?: () => void;

  // ─── Entité invalide (priorité 3 / 4) ──────────────────────────────
  /** True si le QR / payment-link / cagnotte est introuvable ou inaccessible. */
  isEntityInvalid?: boolean;
  /**
   * Type d'entité pour personnaliser l'écran "invalide" :
   *  - "qr"           → "QR Code invalide"
   *  - "payment-link" → "Page de paiement introuvable"
   *  - "campaign"     → "Cagnotte invalide"
   */
  invalidEntityKind?: "qr" | "payment-link" | "campaign";
  /** Titre de l'écran d'erreur (override). */
  invalidTitle?: string;
  /** Message à afficher. */
  invalidMessage?: string;
  /**
   * True si l'asso n'a AUCUN provider de paiement actif (ni Stripe ni
   * PayPal). Distingue l'écran "configurer son compte" vs "URL cassée".
   */
  noPaymentProviderActive?: boolean;
  /**
   * Cagnotte trouvée mais association sans moyen de paiement actif
   * (« Paiements bientôt disponibles »). Sans lui, une cagnotte affichait
   * l'icône rouge de « Cagnotte introuvable » (Karim 2026-10-08).
   */
  paymentsPending?: boolean;

  // ─── Stripe init error (priorité 5) ────────────────────────────────
  /** True si Stripe.js a échoué à s'initialiser. */
  stripeInitError?: boolean;
  /** Message d'erreur retourné par Stripe.js. */
  stripeErrorMessage?: string;
  /**
   * True si l'asso est en mode PayPal-only — dans ce cas, on ignore
   * l'erreur Stripe puisque Stripe n'est pas requis pour cette page.
   */
  isPaypalOnlyMode?: boolean;
  /**
   * True si l'erreur fatale est RÉCUPÉRABLE (network/429/5xx). Dans ce
   * cas on bascule vers le banner inline (toast en haut de page) au lieu
   * du fullscreen. Le contenu de la page reste accessible, l'utilisateur
   * peut continuer à voir le récap, ajuster son don, et retenter.
   * (cf. useProvidersOrchestrator.isRecoverableFatalError)
   */
  isRecoverable?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  orgRiskStatus: null,
  orgRiskReason: null,
  organizationName: null,
  isCampaignPaused: false,
  campaignPausedMessage: "",
  onPausedBack: undefined,
  isEntityInvalid: false,
  invalidEntityKind: "qr",
  invalidTitle: "",
  invalidMessage: "",
  noPaymentProviderActive: false,
  paymentsPending: false,
  stripeInitError: false,
  stripeErrorMessage: "",
  isPaypalOnlyMode: false,
  isRecoverable: false,
});

const emit = defineEmits<{ reload: [] }>();

const { t } = useI18n();

const resolvedCampaignPausedMessage = computed(
  () => props.campaignPausedMessage || t("don.etatsErreur.cagnottePauseMessageDefaut"),
);

// ─── Cascade de priorité ─────────────────────────────────────────────
const isOrgBlocked = computed(
  () =>
    props.orgRiskStatus === "suspended" || props.orgRiskStatus === "banned",
);

/**
 * Vrai si on est en condition "Stripe HS mais en mode PayPal-only" — dans
 * ce cas l'erreur Stripe est non bloquante et on ne l'affiche pas.
 *
 * On exclut aussi les erreurs récupérables (network/429/5xx) : pour celles-là,
 * la page consommatrice affiche un banner inline `<DonateInlineErrorBanner>`
 * au lieu du fullscreen. Le user garde accès au contenu de la page.
 */
const showStripeError = computed(
  () => props.stripeInitError && !props.isPaypalOnlyMode && !props.isRecoverable,
);

/**
 * Variant DonateErrorState pour "entité invalide". Distingue "asso pas
 * configurée" (suggérer de revenir plus tard) vs "URL cassée" (vérifier
 * l'URL).
 */
const invalidErrorType = computed<
  | "payments-pending"
  | "stripe-not-configured"
  | "qr-invalid"
  | "payment-link-invalid"
  | "campaign-invalid"
>(() => {
  // La CIBLE prime sur le provider : quand la cagnotte est introuvable (404 —
  // typiquement un brouillon jamais publié), le chargement force
  // `hasStripeAccount = false`, ce qui allumait `noPaymentProviderActive` et
  // affichait l'écran « Stripe non configuré » — illustration Stripe + bouton
  // « Réessayer » — alors que le compte de l'association fonctionne
  // parfaitement. L'asso croyait son paiement cassé (constaté 2026-09-07).
  if (props.invalidEntityKind === "campaign")
    return props.paymentsPending ? "payments-pending" : "campaign-invalid";
  if (props.noPaymentProviderActive) return "stripe-not-configured";
  if (props.invalidEntityKind === "payment-link")
    return "payment-link-invalid";
  return "qr-invalid";
});

/**
 * True ssi au moins une erreur est active.
 * Pilote aussi l'affichage de la navigation autour (cf. template) : sans
 * erreur, ce composant ne rend rien du tout.
 */
const hasError = computed(
  () =>
    isOrgBlocked.value ||
    props.isCampaignPaused ||
    props.isEntityInvalid ||
    showStripeError.value,
);

defineExpose({ hasError });

function handleReload() {
  emit("reload");
}
</script>

<template>
  <!-- Header + footer autour des états d'erreur (Karim 2026-08-08).
       Les pages de paiement (/donate, /pay, /campaigns/donate, /c/*/payment)
       masquent volontairement la navigation : tunnel épuré façon Stripe
       Checkout, zéro distraction pendant un paiement (cf. `isPaymentPage`
       dans app.vue).
       MAIS quand le paiement est IMPOSSIBLE — association en cours de
       configuration, cagnotte en pause, QR invalide — il n'y a plus de tunnel.
       Le visiteur se retrouvait devant une carte seule au milieu du vide, sans
       aucun moyen de rejoindre le site. On remet donc la navigation
       UNIQUEMENT dans ces états, jamais pendant un paiement : ce composant ne
       rend rien tant qu'il n'y a pas d'erreur.
       ⚠️ Aucune logique de paiement touchée ici — uniquement l'habillage. -->
  <div v-if="hasError">
    <AppHeader :links="navLinks" />

    <!-- Priorité 1 : asso bloquée par Trust & Safety. -->
    <OrgUnavailable
      v-if="isOrgBlocked"
      :risk-status="(orgRiskStatus as RiskStatus)"
      :reason="orgRiskReason"
      :organization-name="organizationName"
    />

    <!-- Priorité 2 : cagnotte en pause. -->
    <DonateErrorState
      v-else-if="isCampaignPaused"
      type="campaign-paused"
      :title="t('don.etatsErreur.cagnottePauseTitre')"
      :message="resolvedCampaignPausedMessage"
      @reload="onPausedBack ? onPausedBack() : handleReload()"
    />

    <!-- Priorité 3 / 4 : entité (QR / payment-link / campagne) invalide. -->
    <DonateErrorState
      v-else-if="isEntityInvalid"
      :type="invalidErrorType"
      :title="invalidTitle"
      :message="invalidMessage"
      @reload="handleReload"
    />

    <!-- Priorité 5 : Stripe.js HS (sauf si PayPal-only). -->
    <DonateErrorState
      v-else-if="showStripeError"
      type="stripe-error"
      :title="t('don.etatsErreur.erreurInitialisationTitre')"
      :message="stripeErrorMessage"
      @reload="handleReload"
    />

    <LazyVisibleAppFooter hydrate-on-visible />
  </div>
</template>
